import { Component, OnInit } from '@angular/core';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import {
  AdminScopes,
  AdminUser,
  UserService,
} from '@app/shared/services/user.service';
import { Router } from '@angular/router';
import { first } from 'rxjs';
import { AwbShipmentRO, ShipmentsService } from '@app/shared/services/shipments.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { DateFormats, DefaultTimezone, PanelWidths } from '@app/shared/constants';
import { environment } from '../../../../../environments/environment';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import { TrackingComponent } from '@app/pages/authenticated/components/tracking/tracking.component';

@Component({
  standalone: false,
  selector: 'app-awb-pickups',
  templateUrl: './awb-pickups.component.html',
})
export class AwbPickupsComponent implements OnInit {
  public loading: boolean = false;
  public user: AdminUser;
  public groupedAWBs: { destinationAirport: string, awbs: AwbShipmentRO[] }[];
  public DATE_FORMAT: string = DateFormats.DATE_SHORT;
  public CDN_URL: string = environment.CDN_URL;
  public defaultTimezone = DefaultTimezone;
  public displayedColumns: string[] = ['shipDate', 'awbNumber', 'lastMileProvider', 'destinationAirport', 'status'];
  public showingTracking: boolean = false;

  constructor(
    private logger: LoggerService,
    private helpersService: HelpersService,
    private bannerNotificationsService: BannerNotificationsService,
    private shipmentsService: ShipmentsService,
    private userService: UserService,
    private router: Router,
    private drawer: MtxDrawer
  ) {}
  ngOnInit() {
    this.userService
      .getAuthenticatedUser([AdminScopes.VIEW_AWB_PICKUPS])
      .pipe(first())
      .subscribe(
        (adminUser: AdminUser) => {
          if (adminUser) {
            this.user = adminUser;
            this.getPickupAWBs();
          } else {
            this.router.navigate(['login']);
          }
        },
        (err) => {
          this.router.navigate(['home']);
        }
      );
  }

  public getPickupAWBs(): void {
    if (!this.loading) {
      this.loading = true;
      this.groupedAWBs = [];
      this.shipmentsService.getPickupAWBs().subscribe(
        (awbs: any[]) => {
          this.logger.log('GET PickupAWBs success', awbs);
          awbs.forEach(awb => {
            awb.lastMileProviderName = this.helpersService.convertShipmentProviderToString(awb.lastMileProvider);
            if (!this.groupedAWBs.find(group => group.destinationAirport === awb.destinationAirport)) {
              this.groupedAWBs.push({ destinationAirport: awb.destinationAirport, awbs: [awb] });
            } else {
              this.groupedAWBs.find(group => group.destinationAirport === awb.destinationAirport).awbs.push(awb);
            }
          });
          this.groupedAWBs.sort((a, b) => b.destinationAirport.localeCompare(a.destinationAirport));
          this.loading = false;
        },
        (err) => {
          this.bannerNotificationsService.error('Could not get list of AWBs', {
            category: 'GET Pickup AWBs error',
            message: err,
          });
          this.loading = false;
        },
      );
    }
  }

  public openTrackingFromAWB(awb: AwbShipmentRO): void {
    if (this.showingTracking) {
      return;
    }

    this.showingTracking = true;
    const queryParams = {
      tracking_number: awb.awbNumber,
      include_admin_charge_details: true,
    };

    this.shipmentsService.findByTracking(queryParams).subscribe(
      (shipment: any) => {
        if (!shipment) {
          this.bannerNotificationsService.error('Could not find shipment for this AWB number', {
            category: 'Tracking error',
            message: 'No shipment found',
          });
          this.showingTracking = false;
          return;
        }

        if (!shipment.provider || !shipment.companyAndUserInfo?.company) {
          this.bannerNotificationsService.error('Shipment data is incomplete', {
            category: 'Tracking error',
            message: 'Missing required shipment information',
          });
          this.showingTracking = false;
          return;
        }

        const drawerRef = this.drawer.open(TrackingComponent, {
          width: PanelWidths.desktopFull,
          disableClose: true,
          closeOnNavigation: false,
          data: {
            shipment: shipment,
            trackingNumber: shipment.tracking,
            provider: shipment.provider,
            company: shipment.companyAndUserInfo.company,
            shouldUpdateTrackingStatus: true,
          },
        });

        drawerRef.afterDismissed().subscribe(() => {
          this.showingTracking = false;
        });
      },
      (err: Error) => {
        this.bannerNotificationsService.error('Could not load tracking information', {
          category: 'Tracking error',
          message: err,
        });
        this.showingTracking = false;
      }
    );
  }
}
